import type { MessagesQuota } from 'types/account/messagesQuota';
import type { PaymentMethod, SubscriptionPlan, UsageQuota } from 'types/account/subscriptionDetails';

/** Module keys returned by the Make.com account-modules webhook. */
export type AccountModuleId =
  | 'massMessaging'
  | 'bots'
  | 'aiBots'
  | 'documentSigning'
  | 'priority'
  | 'ai'
  /** בונה בוטים ב-AI (צ'אט בבוט בילדר). opt-in לחברה; ברירת מחדל כבוי. */
  | 'aiBotBuilder'
  /** סוכן ההזמנות (פיילוט). opt-in לחברה; ברירת מחדל כבוי, ומוסתר לגמרי כשכבוי. */
  | 'ordersAgent'
  /** פאנל קריאות שירות בצ'אט (פריוריטי/פיירברי). opt-in לחברה; מוסתר כשכבוי. */
  | 'serviceCalls';

export type AccountSystemStatus = 'trial' | 'active' | 'inactive';

/** Reason the account is blocked (part B enforcement); null = not blocked. */
export type AccountBlockedReason = 'TRIAL_EXPIRED' | 'INACTIVE';

export type AccountModulesPayload = {
  modules: Record<AccountModuleId, boolean>;
  /** When set, the server has blocked the account (trial expired / inactive) and all modules return off. */
  blockedReason: AccountBlockedReason | null;
  systemStatus: AccountSystemStatus;
  /** ISO date (YYYY-MM-DD) or null when not in trial. */
  trialEndDate: string | null;
  /** Outgoing message quota for the account; null when not returned by the webhook. */
  messagesQuota: MessagesQuota | null;
  /** Current subscription plan and billing cycle; null when not returned by the webhook. */
  subscription: SubscriptionPlan | null;
  /** Saved payment method; null when billing provider is not connected. */
  paymentMethod: PaymentMethod | null;
  /** Contacts included in the plan; null when not returned by the webhook. */
  contactsQuota: UsageQuota | null;
  /** Active bots included in the plan; null when not returned by the webhook. */
  botsQuota: UsageQuota | null;
  /** Broadcasts allowed per calendar month; null = unlimited. */
  broadcastsQuota: UsageQuota | null;
  /** Max contacts allowed in a single distribution list; null = unlimited. */
  distributionListMaxSize: number | null;
};

export const ACCOUNT_MODULE_IDS: AccountModuleId[] = [
  'massMessaging',
  'bots',
  'aiBots',
  'documentSigning',
  'priority',
  'ai',
  'aiBotBuilder',
  'ordersAgent',
  'serviceCalls',
];

/**
 * מודולים שנוספו אחרי שהחוזה נסגר: אם השרת שנפרס עדיין לא מחזיר אותם, הפרסר
 * לא פוסל את כל התשובה אלא מניח שהם כבויים. מונע שבירה בזמן פריסה לא מסונכרנת
 * של פרונט לפני נוד.
 */
export const OPTIONAL_ACCOUNT_MODULE_IDS: ReadonlySet<AccountModuleId> = new Set(['aiBotBuilder', 'ordersAgent', 'serviceCalls']);

/**
 * מודולי פיילוט שלא מוצגים ללקוח ברישום היכולות (מסך "מודולים בחשבון") —
 * חברה שלא קיבלה אותם לא אמורה לדעת שהם קיימים, ולכן גם אין טיזר שדרוג.
 */
export const HIDDEN_ACCOUNT_MODULE_IDS: ReadonlySet<AccountModuleId> = new Set(['ordersAgent', 'serviceCalls']);

export const ACCOUNT_SYSTEM_STATUSES: AccountSystemStatus[] = ['trial', 'active', 'inactive'];

export function isAccountModuleEnabled(
  data: AccountModulesPayload | null | undefined,
  moduleId: AccountModuleId,
): boolean {
  return data?.modules[moduleId] === true;
}
